// src/services/billingService.js
import axios from "axios";
import apiClient from "./apiClient";
import { BACKEND_URL } from "../utils/envConfig";

const API_URL = `${BACKEND_URL}/billing`;

// Bill Management
export const createBill = async (billData) => {
  const response = await axios.post(`${API_URL}/bills`, billData);
  return response.data;
};

export const getBillById = async (billId) => {
  const response = await axios.get(`${API_URL}/bills/${billId}`);
  return response.data;
};

export const updateBill = async (billId, billData) => {
  // Use apiClient to automatically add organizationId
  // apiClient already has baseURL, so use relative path
  const response = await apiClient.put(`/billing/bills/${billId}`, billData);
  return response.data;
};

export const deleteBill = async (billId) => {
  const response = await axios.delete(`${API_URL}/bills/${billId}`);
  return response.data;
};

// Bill Details Management
export const addBillDetail = async (billId, detailData) => {
  const response = await axios.post(`${API_URL}/bills/${billId}/details`, detailData);
  return response.data;
};

export const updateBillDetail = async (billId, detailId, detailData) => {
  const response = await axios.put(`${API_URL}/bills/${billId}/details/${detailId}`, detailData);
  return response.data;
};

export const deleteBillDetail = async (billId, detailId) => {
  const response = await axios.delete(`${API_URL}/bills/${billId}/details/${detailId}`);
  return response.data;
};

// Payment Processing
export const processCashPayment = async (billId, paymentData) => {
  // Use apiClient to automatically add organizationId
  // apiClient already has baseURL, so use relative path
  const response = await apiClient.post(`/billing/bills/${billId}/cash-payment`, paymentData);
  return response.data;
};

export const processCreditPayment = async (billId, paymentData) => {
  // Use apiClient to automatically add organizationId
  // apiClient already has baseURL, so use relative path
  const response = await apiClient.post(`/billing/bills/${billId}/credit-payment`, paymentData);
  return response.data;
};

// Invoice Management
export const createInvoice = async (billId, invoiceData) => {
  const response = await axios.post(`${API_URL}/bills/${billId}/invoice`, invoiceData);
  return response.data;
};

export const getInvoiceById = async (invoiceId) => {
  const response = await axios.get(`${API_URL}/invoices/${invoiceId}`);
  return response.data;
};

export const updateInvoiceStatus = async (invoiceId, status) => {
  const response = await axios.put(`${API_URL}/invoices/${invoiceId}/status`, { status });
  return response.data;
};

// Department Settlement
export const createDepartmentSettlement = async (invoiceId, settlementData) => {
  const response = await axios.post(`${API_URL}/invoices/${invoiceId}/settlement`, settlementData);
  return response.data;
};

export const getDepartmentSettlements = async (invoiceId) => {
  const response = await axios.get(`${API_URL}/invoices/${invoiceId}/settlements`);
  return response.data;
};

export const markDepartmentSettlementComplete = async (invoiceId, departmentId) => {
  const response = await axios.put(`${API_URL}/invoices/${invoiceId}/settlements/${departmentId}/complete`);
  return response.data;
};

// IPD Service Tracking
export const addIPDService = async (admissionId, serviceData) => {
  const response = await axios.post(`${API_URL}/ipd/${admissionId}/services`, serviceData);
  return response.data;
};

export const getIPDServices = async (admissionId, organizationId = null) => {
  // Validate organizationId - only use if it's a valid positive number
  let validOrgId = null;
  if (organizationId != null) {
    const orgId = Number(organizationId);
    if (!isNaN(orgId) && orgId > 0) {
      validOrgId = orgId;
    }
  }
  
  // If organizationId not provided, try to get from localStorage (for common users)
  if (!validOrgId) {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        const orgId = user?.organizationId;
        if (orgId != null && orgId !== "" && !isNaN(orgId) && Number(orgId) > 0) {
          validOrgId = Number(orgId);
        }
      } catch (e) {
        // Ignore parse errors
      }
    }
  }
  
  // Build query parameters
  const params = new URLSearchParams();
  // Only add organizationId if valid (common users won't have it)
  if (validOrgId && validOrgId > 0) {
    params.append('organizationId', validOrgId);
  }
  
  const url = `${API_URL}/ipd/${admissionId}/services${params.toString() ? '?' + params.toString() : ''}`;
  const response = await axios.get(url);
  return response.data;
};

export const generateIPDDischargeBill = async (admissionId, tariffId, paymentMode, organizationId = null, services = [], createdBy = null, creditOrganizationId = null) => {
  // Validate organizationId - only use if it's a valid positive number
  let validOrgId = null;
  if (organizationId != null) {
    const orgId = Number(organizationId);
    if (!isNaN(orgId) && orgId > 0) {
      validOrgId = orgId;
    }
  }
  
  // If organizationId not provided, try to get from localStorage (for common users)
  if (!validOrgId) {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        const orgId = user?.organizationId;
        if (orgId != null && orgId !== "" && !isNaN(orgId) && Number(orgId) > 0) {
          validOrgId = Number(orgId);
        }
      } catch (e) {
        // Ignore parse errors
      }
    }
  }
  
  // Build query parameters (backend expects these as request parameters)
  const params = new URLSearchParams();
  if (tariffId != null) {
    params.append('tariffId', tariffId);
  }
  if (paymentMode) {
    // Backend expects uppercase enum values (CASH, CREDIT)
    params.append('paymentMode', paymentMode.toUpperCase());
  }
  // Only add organizationId if valid (common users won't have it)
  if (validOrgId && validOrgId > 0) {
    params.append('organizationId', validOrgId);
  }
  if (createdBy != null) {
    params.append('createdBy', createdBy);
  }
  
  // Sanitize services: only send billing-relevant fields to avoid
  // backend deserialization issues from extra API response fields
  const sanitizedServices = (services || []).map(s => ({
    type: s.type || '',
    description: s.description || '',
    quantity: Number(s.quantity) || 1,
    rate: Number(s.rate) || 0,
    amount: Number(s.amount) || 0,
    ...(s.department ? { department: s.department } : {}),
  }));
  
  const url = `${API_URL}/ipd/${admissionId}/discharge-bill${params.toString() ? '?' + params.toString() : ''}`;
  const body = {
    services: sanitizedServices,
  };
  // For credit payments, include the credit organization ID in the body
  if (creditOrganizationId != null) {
    const creditOrgId = Number(creditOrganizationId);
    if (!isNaN(creditOrgId) && creditOrgId > 0) {
      body.creditOrganizationId = creditOrgId;
    }
  }
  const response = await axios.post(url, body);
  return response.data;
};

// OPD Service Billing
export const createOPDBill = async (patientId, visitId, services, tariffId, paymentMode, organizationId = null) => {
  // Validate organizationId - only use if it's a valid positive number
  let validOrgId = null;
  if (organizationId != null) {
    const orgId = Number(organizationId);
    if (!isNaN(orgId) && orgId > 0) {
      validOrgId = orgId;
    }
  }
  
  // If organizationId not provided, try to get from localStorage (for common users)
  if (!validOrgId) {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        const orgId = user?.organizationId;
        if (orgId != null && orgId !== "" && !isNaN(orgId) && Number(orgId) > 0) {
          validOrgId = Number(orgId);
        }
      } catch (e) {
        // Ignore parse errors
      }
    }
  }
  
  const response = await axios.post(`${API_URL}/opd/bill`, {
    patientId,
    visitId,
    services,
    tariffId,
    // Backend expects uppercase enum values (CASH, CREDIT)
    paymentMode: paymentMode ? paymentMode.toUpperCase() : paymentMode,
    // Only include organizationId if valid (common users won't have it)
    ...(validOrgId && validOrgId > 0 && { organizationId: validOrgId })
  });
  return response.data;
};

// Bill Status Management
export const updateBillStatus = async (billId, status) => {
  const response = await axios.put(`${API_URL}/bills/${billId}/status`, { status });
  return response.data;
};

export const getBillsByStatus = async (status) => {
  const response = await axios.get(`${API_URL}/bills/status/${status}`);
  return response.data;
};

export const getBillsByPatient = async (patientId) => {
  const response = await axios.get(`${API_URL}/bills/patient/${patientId}`);
  return response.data;
};

export const getBillsByOrganization = async (organizationId) => {
  const response = await axios.get(`${API_URL}/bills/organization/${organizationId}`);
  return response.data;
};

// Outstanding Bills
export const getOutstandingBills = async () => {
  const response = await axios.get(`${API_URL}/bills/outstanding`);
  return response.data;
};

export const getOutstandingBillsByOrganization = async (organizationId) => {
  const response = await axios.get(`${API_URL}/bills/outstanding/organization/${organizationId}`);
  return response.data;
};

export default {
  createBill,
  getBillById,
  updateBill,
  deleteBill,
  addBillDetail,
  updateBillDetail,
  deleteBillDetail,
  processCashPayment,
  processCreditPayment,
  createInvoice,
  getInvoiceById,
  updateInvoiceStatus,
  createDepartmentSettlement,
  getDepartmentSettlements,
  markDepartmentSettlementComplete,
  addIPDService,
  getIPDServices,
  generateIPDDischargeBill,
  createOPDBill,
  updateBillStatus,
  getBillsByStatus,
  getBillsByPatient,
  getBillsByOrganization,
  getOutstandingBills,
  getOutstandingBillsByOrganization
};
